// languagetools:disable
import type { IAddressDetails, IShipmentFile, TShipmentFiles } from '../../../../../types/shipment'

export interface IFileMetadataEntry {
  label: string
  value: string
}

// cspell:ignore tarped
const isShipmentFile = (value: unknown): value is IShipmentFile => {
  if (!value || typeof value !== 'object') return false

  const candidate = value as Partial<IShipmentFile>

  return typeof candidate.url === 'string' && typeof candidate.classificationType === 'string'
}

export const normalizeShipmentFiles = (files: TShipmentFiles): IShipmentFile[] => {
  if (!files) return []

  const normalizedFiles = Array.isArray(files)
    ? files.filter(isShipmentFile)
    : Object.values(files).filter(isShipmentFile)

  return normalizedFiles.sort((leftFile, rightFile) => {
    const leftTimestamp = new Date(leftFile.timestamp).getTime()
    const rightTimestamp = new Date(rightFile.timestamp).getTime()

    if (Number.isNaN(leftTimestamp) && Number.isNaN(rightTimestamp)) return 0
    if (Number.isNaN(leftTimestamp)) return 1
    if (Number.isNaN(rightTimestamp)) return -1

    return rightTimestamp - leftTimestamp
  })
}

export const getDisplayFileName = (fileName: string) =>
  fileName.replace(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.(.+)$/i, '$1')

export const getMimeTypeFromFileName = (fileName: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase()

  switch (extension) {
    case 'gif':
      return 'image/gif'
    case 'jpeg':
    case 'jpg':
      return 'image/jpeg'
    case 'mov':
      return 'video/quicktime'
    case 'mp4':
      return 'video/mp4'
    case 'pdf':
      return 'application/pdf'
    case 'png':
      return 'image/png'
    case 'svg':
      return 'image/svg+xml'
    case 'webm':
      return 'video/webm'
    case 'webp':
      return 'image/webp'
    default:
      return 'application/octet-stream'
  }
}

export const getFileExtension = (url: string) => {
  const fileName = url.split('/').pop()?.split('?')[0]?.trim().toLowerCase() ?? 'file'
  const extension = fileName.split('.').pop()?.trim().toLowerCase()

  if (!extension || extension === fileName) {
    return 'file'
  }

  return extension
}

export const truncateText = (value: string, maxLength = 16) => {
  if (value.length <= maxLength) return value

  return `${value.slice(0, maxLength - 1)}...`
}

export const isImagePreview = (url: string) =>
  ['avif', 'bmp', 'gif', 'jpeg', 'jpg', 'png', 'svg', 'webp'].includes(getFileExtension(url))

export const isPdfPreview = (url: string) => getFileExtension(url) === 'pdf'

export const isVideoPreview = (url: string) => ['m4v', 'mov', 'mp4', 'ogg', 'webm'].includes(getFileExtension(url))

export const formatTimestamp = (timestamp?: string) => {
  if (!timestamp) return null

  const parsedDate = new Date(timestamp)

  if (Number.isNaN(parsedDate.getTime())) return null

  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(parsedDate)
}

const formatAddressDetails = (address?: IAddressDetails): string | undefined => {
  if (!address) return undefined

  const parts = [address.name, address.city, address.state].filter(Boolean)

  return parts.length > 0 ? parts.join(', ') : undefined
}

const pushMetadataEntry = (entries: IFileMetadataEntry[], label: string, value?: boolean | null | number | string) => {
  if (value === undefined || value === null || value === '') return

  entries.push({
    label,
    value: typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)
  })
}

export const getExtractedDataEntries = (file: IShipmentFile): IFileMetadataEntry[] => {
  const extractedData = file.extractedData

  if (!extractedData || typeof extractedData !== 'object') return []

  const entries: IFileMetadataEntry[] = []

  if ('billOfLadingNumber' in extractedData) {
    pushMetadataEntry(entries, 'BOL', extractedData.billOfLadingNumber)
    pushMetadataEntry(entries, 'Booking', extractedData.bookingNumber)
    pushMetadataEntry(entries, 'PO', extractedData.poNumber)
    pushMetadataEntry(entries, 'Shipment', extractedData.shipmentNumber)
    pushMetadataEntry(entries, 'Port of loading', extractedData.portOfLoading)
    pushMetadataEntry(entries, 'Voyage', extractedData.voyageNumber)
    pushMetadataEntry(entries, 'Container', extractedData.container?.containerNumber)
    pushMetadataEntry(entries, 'Container type', extractedData.container?.containerType)
    pushMetadataEntry(entries, 'Container size', extractedData.container?.containerSize)
    pushMetadataEntry(entries, 'Chassis', extractedData.container?.chassisNumber)
    pushMetadataEntry(entries, 'Commodity', extractedData.commodityDetails?.description)
    pushMetadataEntry(
      entries,
      'Commodity qty',
      extractedData.commodityDetails?.quantity != null
        ? `${extractedData.commodityDetails.quantity}${extractedData.commodityDetails.unit ? ` ${extractedData.commodityDetails.unit}` : ''}`
        : undefined
    )
    pushMetadataEntry(
      entries,
      'Weight',
      extractedData.commodityDetails?.weight != null
        ? `${extractedData.commodityDetails.weight}${extractedData.commodityDetails.weightUnit ? ` ${extractedData.commodityDetails.weightUnit}` : ''}`
        : undefined
    )
    pushMetadataEntry(entries, 'Shipper', formatAddressDetails(extractedData.shipper))
    pushMetadataEntry(entries, 'Consignee', formatAddressDetails(extractedData.consignee))
  }

  if ('hasAccessorialCosts' in extractedData) {
    pushMetadataEntry(entries, 'Accessorials', extractedData.hasAccessorialCosts)
  }

  if ('amount' in extractedData) {
    pushMetadataEntry(entries, 'Amount', extractedData.amount)
  }

  if ('description' in extractedData || 'isChained' in extractedData || 'trailerNumber' in extractedData) {
    pushMetadataEntry(entries, 'Description', extractedData.description)
    pushMetadataEntry(entries, 'Trailer', extractedData.trailerNumber)
    pushMetadataEntry(entries, 'Trailer type', extractedData.trailerType)
    pushMetadataEntry(entries, 'Chained', extractedData.isChained)
    pushMetadataEntry(entries, 'Strapped', extractedData.isStrapped)
    pushMetadataEntry(entries, 'Tarped', extractedData.isTarped)
  }

  if ('sealNumber' in extractedData) {
    pushMetadataEntry(entries, 'Seal', extractedData.sealNumber)
  }

  if ('exceptionDescription' in extractedData || 'exceptionSeverity' in extractedData) {
    pushMetadataEntry(entries, 'Exception', extractedData.exceptionType)
    pushMetadataEntry(entries, 'Severity', extractedData.exceptionSeverity)
    pushMetadataEntry(entries, 'Damage', extractedData.isFreightDamaged)
    pushMetadataEntry(entries, 'Notes', extractedData.exceptionDescription)
  }

  if ('interchangeNumber' in extractedData || 'equipmentDetails' in extractedData) {
    pushMetadataEntry(entries, 'Interchange', extractedData.interchangeNumber)
    pushMetadataEntry(entries, 'Equipment', extractedData.equipmentDetails?.equipmentType)
    pushMetadataEntry(entries, 'Truck', extractedData.equipmentDetails?.truckNumber)
    pushMetadataEntry(entries, 'Sealed', extractedData.equipmentDetails?.isSealed)
    pushMetadataEntry(entries, 'Description', extractedData.equipmentDetails?.equipmentDescription)
    pushMetadataEntry(entries, 'Chassis', extractedData.equipmentDetails?.chassisNumbers?.join(', '))
  }

  if ('container' in extractedData && extractedData.container && !('billOfLadingNumber' in extractedData)) {
    pushMetadataEntry(entries, 'Container', extractedData.container.containerNumber)
    pushMetadataEntry(entries, 'Container type', extractedData.container.containerType)
    pushMetadataEntry(entries, 'Container size', extractedData.container.containerSize)
    pushMetadataEntry(entries, 'Chassis', extractedData.container.chassisNumber)
  }

  return entries
}
